import { Code, ConnectError } from '@connectrpc/connect'
import type { Transport } from '@connectrpc/connect'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import type { QueryClient } from '@tanstack/react-query'
import type { MessageInitShape, DescMessage } from '@bufbuild/protobuf'

/**
 * Smart retry function that skips retries for unrecoverable errors
 *
 * Use this instead of `retry: 1` for more intelligent retry behavior:
 * - Skips retries on NOT_FOUND (404)
 * - Skips retries on UNAUTHENTICATED (401)
 * - Skips retries on PERMISSION_DENIED (403)
 * - Skips retries on FAILED_PRECONDITION (412 — user must act, e.g. reconnect Gmail)
 * - Skips retries on RESOURCE_EXHAUSTED (429 — client back off on rate limit)
 * - Skips retries on UNKNOWN (some connect-web versions map fetch failures here)
 * - Skips retries on non-ConnectError exceptions — these are network/CORS
 *   failures (TypeError "Failed to fetch") where the server response is
 *   unreadable, so the response code can't be inspected. Retrying amplifies
 *   the storm; window-focus refetch and manual refresh recover when the
 *   network is back.
 * - Retries up to 3 times for other errors
 *
 * @example
 * ```typescript
 * const query = useQuery(getEvent, request, {
 *   retry: smartRetry,
 * })
 * ```
 */
export const smartRetry = (failureCount: number, error: Error): boolean => {
  if (!(error instanceof ConnectError)) {
    return false
  }
  const nonRetryableCodes = [
    Code.NotFound,
    Code.Unauthenticated,
    Code.PermissionDenied,
    Code.InvalidArgument,
    Code.FailedPrecondition,
    Code.ResourceExhausted,
    Code.Unknown,
  ]
  if (nonRetryableCodes.includes(error.code)) {
    return false
  }
  return failureCount < 3
}

/**
 * Context returned from onMutate for use in onError rollback
 */
export interface OptimisticContext {
  previousData: unknown
  queryKey: unknown[]
}

/**
 * Creates a query key for Connect-RPC queries using createConnectQueryKey
 *
 * This is a convenience wrapper that provides proper typing and defaults.
 *
 * @example
 * ```typescript
 * const queryKey = createQueryKeyForGet({
 *   schema: getEvent,
 *   transport,
 *   input: { id: eventId },
 * })
 * ```
 */
export function createQueryKeyForGet<I extends DescMessage>({
  schema,
  transport,
  input,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any
  transport: Transport
  input: MessageInitShape<I>
}) {
  return createConnectQueryKey({
    schema,
    transport,
    input,
    cardinality: 'finite',
  })
}

/**
 * Creates a query key for list queries (for invalidation)
 *
 * @example
 * ```typescript
 * const listKey = createQueryKeyForList({
 *   schema: listEvents,
 *   transport,
 * })
 * queryClient.invalidateQueries({ queryKey: listKey })
 * ```
 */
export function createQueryKeyForList({
  schema,
  transport,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any
  transport: Transport
}) {
  return createConnectQueryKey({
    schema,
    transport,
    cardinality: undefined, // Matches both finite and infinite
  })
}

/**
 * Cancels in-flight queries and returns snapshot for rollback
 *
 * Use in onMutate for optimistic updates:
 *
 * @example
 * ```typescript
 * onMutate: async (variables) => {
 *   return await prepareOptimisticUpdate({
 *     queryClient,
 *     queryKey: createQueryKeyForGet({ schema: getEvent, transport, input: { id: variables.id } }),
 *   })
 * }
 * ```
 */
export async function prepareOptimisticUpdate({
  queryClient,
  queryKey,
}: {
  queryClient: QueryClient
  queryKey: unknown[]
}): Promise<OptimisticContext> {
  // Cancel in-flight queries to prevent race conditions
  await queryClient.cancelQueries({ queryKey })

  // Snapshot previous value for rollback
  const previousData = queryClient.getQueryData(queryKey)

  return { previousData, queryKey }
}

/**
 * Rolls back cache to previous state on mutation error
 *
 * Use in onError callback:
 *
 * @example
 * ```typescript
 * onError: (_error, _variables, context) => {
 *   rollbackOptimisticUpdate({ queryClient, context })
 * }
 * ```
 */
export function rollbackOptimisticUpdate({
  queryClient,
  context,
}: {
  queryClient: QueryClient
  context?: OptimisticContext
}): void {
  if (context?.previousData !== undefined && context?.queryKey) {
    queryClient.setQueryData(context.queryKey, context.previousData)
  }
}

/**
 * Updates all list caches with a modified item
 *
 * Finds all cached lists matching the schema and updates the item in each.
 *
 * @example
 * ```typescript
 * updateListCaches({
 *   queryClient,
 *   transport,
 *   listSchema: listEvents,
 *   listKey: 'events',
 *   updatedItem: response.event,
 *   matchFn: (item) => item.id === response.event.id,
 *   createListResponse: (events) => create(ListEventsResponseSchema, { events }),
 * })
 * ```
 */
export function updateListCaches<TItem, TListResponse>({
  queryClient,
  transport,
  listSchema,
  listKey,
  updatedItem,
  matchFn,
  createListResponse,
}: {
  queryClient: QueryClient
  transport: Transport
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listSchema: any
  listKey: string
  updatedItem: TItem
  matchFn: (item: TItem) => boolean
  createListResponse: (items: TItem[]) => TListResponse
}): void {
  const listQueryFilter = createConnectQueryKey({
    schema: listSchema,
    transport,
    cardinality: undefined,
  })

  // Get all active list queries
  const existingLists = queryClient.getQueriesData<Record<string, TItem[]>>({
    queryKey: listQueryFilter,
  })

  // Update each list with the modified item
  existingLists.forEach(([queryKey, oldData]) => {
    if (!oldData?.[listKey]) return

    // Replace the matching item in this list
    const updatedList = oldData[listKey].map((item) =>
      matchFn(item) ? updatedItem : item
    )

    // Set updated list back to cache
    queryClient.setQueryData(queryKey, createListResponse(updatedList))
  })
}

/**
 * Adds an item to all list caches
 *
 * @example
 * ```typescript
 * addToListCaches({
 *   queryClient,
 *   transport,
 *   listSchema: listEvents,
 *   listKey: 'events',
 *   newItem: response.event,
 *   createListResponse: (events) => create(ListEventsResponseSchema, { events }),
 * })
 * ```
 */
export function addToListCaches<TItem, TListResponse>({
  queryClient,
  transport,
  listSchema,
  listKey,
  newItem,
  createListResponse,
}: {
  queryClient: QueryClient
  transport: Transport
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listSchema: any
  listKey: string
  newItem: TItem
  createListResponse: (items: TItem[]) => TListResponse
}): void {
  const listQueryFilter = createConnectQueryKey({
    schema: listSchema,
    transport,
    cardinality: undefined,
  })

  const existingLists = queryClient.getQueriesData<Record<string, TItem[]>>({
    queryKey: listQueryFilter,
  })

  existingLists.forEach(([queryKey, oldData]) => {
    if (!oldData?.[listKey]) return

    const updatedList = [...oldData[listKey], newItem]
    queryClient.setQueryData(queryKey, createListResponse(updatedList))
  })
}

/**
 * Removes an item from all list caches
 *
 * @example
 * ```typescript
 * removeFromListCaches({
 *   queryClient,
 *   transport,
 *   listSchema: listEvents,
 *   listKey: 'events',
 *   matchFn: (item) => item.id === deletedId,
 *   createListResponse: (events) => create(ListEventsResponseSchema, { events }),
 * })
 * ```
 */
export function removeFromListCaches<TItem, TListResponse>({
  queryClient,
  transport,
  listSchema,
  listKey,
  matchFn,
  createListResponse,
}: {
  queryClient: QueryClient
  transport: Transport
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listSchema: any
  listKey: string
  matchFn: (item: TItem) => boolean
  createListResponse: (items: TItem[]) => TListResponse
}): void {
  const listQueryFilter = createConnectQueryKey({
    schema: listSchema,
    transport,
    cardinality: undefined,
  })

  const existingLists = queryClient.getQueriesData<Record<string, TItem[]>>({
    queryKey: listQueryFilter,
  })

  existingLists.forEach(([queryKey, oldData]) => {
    if (!oldData?.[listKey]) return

    const updatedList = oldData[listKey].filter((item) => !matchFn(item))
    queryClient.setQueryData(queryKey, createListResponse(updatedList))
  })
}
